import { useCallback, useEffect, useState } from "react";
import SortableTableV2 from "../SortableTableV2";

function loadTableState(storageKey) {
  if (!storageKey) return {};
  try {
    const raw = sessionStorage.getItem(`datatable_${storageKey}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function DataTable({
  columns,
  fetchData,
  filters = {},
  defaultLimit = 10,
  defaultOrderBy = "",
  defaultOrder = "asc",
  expandable = false,
  renderExpandedContent,
  onRowClick = false,
  storageKey,
}) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  const [_tableState] = useState(() => loadTableState(storageKey));

  const [page, setPage] = useState(_tableState.page ?? 0);
  const [limit, setLimit] = useState(_tableState.limit ?? defaultLimit);
  const [order, setOrder] = useState(_tableState.order ?? defaultOrder);
  const [orderBy, setOrderBy] = useState(_tableState.orderBy ?? defaultOrderBy);

  useEffect(() => {
    if (!storageKey) return;
    sessionStorage.setItem(`datatable_${storageKey}`, JSON.stringify({ page, limit, order, orderBy }));
  }, [storageKey, page, limit, order, orderBy]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData({
        page: page + 1,
        limit,
        ...filters,
        ...(orderBy && { columnName: orderBy, order }),
      });
      setData(res?.data ?? []);
      setTotal(res?.total ?? 0);
      setTotalPages(Math.ceil(res.total / res.limit));
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters, order, orderBy, fetchData]);

  useEffect(() => {
    loadData();
  }, [loadData]);


  const handleSort = useCallback((col, direction) => {
    setPage(0);
    setOrderBy(col);
    setOrder(direction);
  }, []);

  const handleLimitChange = useCallback((val) => {
    setLimit(val);
    setPage(0);
  }, []);

  return (
    <SortableTableV2
      columns={columns}
      data={data}
      totalData={total}
      totalPages={totalPages}
      loading={loading}
      limit={limit}
      currentPage={page + 1}
      setLimit={handleLimitChange}
      handlePageClick={(e) => setPage(e.selected)}
      order={order}
      orderBy={orderBy}
      onSort={handleSort}
      expandable={expandable}
      renderExpandedContent={renderExpandedContent}
      onRowClick={onRowClick}
    />
  );
}
